/**
 * Token Helper Utility
 *
 * Follows SOLID Principles:
 * - Single Responsibility: Provides token access methods only
 * - Interface Segregation: Separate hooks for different use cases
 * - Dependency Inversion: Depends on store and storage abstractions
 *
 * Provides a centralized way to access tokens for API calls.
 * Ensures tokens are always retrieved from secure storage.
 */

import { useAuthStore } from '@/store/authStore';
import { getSecureToken, TOKEN_KEYS } from './secureTokenStorage';

// ============================================================================
// Interfaces (Interface Segregation Principle)
// ============================================================================

/**
 * Interface for token retrieval operations
 */
interface ITokenProvider {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  getTokens(): { accessToken: string | null; refreshToken: string | null };
}

// ============================================================================
// React Hooks (For React Components)
// ============================================================================

/**
 * Gets access token from store (for React components)
 * Interface Segregation: Single purpose hook
 */
export const useAccessToken = (): string | null => {
  return useAuthStore(state => state.accessToken);
};

/**
 * Gets refresh token from store (for React components)
 * Interface Segregation: Single purpose hook
 */
export const useRefreshToken = (): string | null => {
  return useAuthStore(state => state.refreshToken);
};

/**
 * Gets both tokens from store (for React components)
 * Interface Segregation: Combined hook for convenience
 */
export const useAuthTokens = (): {
  accessToken: string | null;
  refreshToken: string | null;
} => {
  const accessToken = useAuthStore(state => state.accessToken);
  const refreshToken = useAuthStore(state => state.refreshToken);
  return { accessToken, refreshToken };
};

/**
 * Gets authentication state (for React components)
 */
export const useAuthState = (): {
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
} => {
  const accessToken = useAuthStore(state => state.accessToken);
  const refreshToken = useAuthStore(state => state.refreshToken);
  return {
    accessToken,
    refreshToken,
    isAuthenticated: !!(accessToken && refreshToken),
  };
};

// ============================================================================
// Direct Access Functions (For Non-React Code)
// ============================================================================

/**
 * Token Provider Implementation
 * Single Responsibility: Provides token access
 * Dependency Inversion: Uses secure storage abstraction
 */
class TokenProvider implements ITokenProvider {
  getAccessToken(): string | null {
    return getSecureToken(TOKEN_KEYS.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return getSecureToken(TOKEN_KEYS.REFRESH_TOKEN);
  }

  getTokens(): { accessToken: string | null; refreshToken: string | null } {
    return {
      accessToken: this.getAccessToken(),
      refreshToken: this.getRefreshToken(),
    };
  }
}

// Singleton instance
let tokenProviderInstance: ITokenProvider | null = null;

/**
 * Gets or creates the token provider instance
 */
const getTokenProvider = (): ITokenProvider => {
  if (!tokenProviderInstance) {
    tokenProviderInstance = new TokenProvider();
  }
  return tokenProviderInstance;
};

/**
 * Gets access token directly from secure storage (for non-React code)
 */
export const getAccessToken = (): string | null => {
  return getTokenProvider().getAccessToken();
};

/**
 * Gets refresh token directly from secure storage (for non-React code)
 */
export const getRefreshToken = (): string | null => {
  return getTokenProvider().getRefreshToken();
};

/**
 * Gets both tokens directly from secure storage (for non-React code)
 */
export const getAuthTokens = (): {
  accessToken: string | null;
  refreshToken: string | null;
} => {
  return getTokenProvider().getTokens();
};

// ============================================================================
// API Request Helpers
// ============================================================================

/**
 * Creates headers with authentication tokens
 * Single Responsibility: Header creation only
 */
export const createAuthHeaders = (options?: {
  accessToken?: string | null;
  refreshToken?: string | null;
}): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const accessToken = options?.accessToken ?? getAccessToken();
  const refreshToken = options?.refreshToken ?? getRefreshToken();

  if (accessToken) {
    headers['x-access-token'] = accessToken;
  }

  if (refreshToken) {
    headers['x-refresh-token'] = refreshToken;
  }

  return headers;
};

/**
 * Creates Authorization Bearer header
 * Single Responsibility: Bearer token header creation only
 */
export const createBearerHeader = (
  token?: string | null
): Record<string, string> => {
  const accessToken = token ?? getAccessToken();
  if (!accessToken) {
    return {};
  }
  return {
    Authorization: `Bearer ${accessToken}`,
  };
};
